import React, { useState } from "react";
import { Tab } from "@headlessui/react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { toggleTheme, setTheme } from "../store/slices/themeSlice";
import {
  PaintBrushIcon,
  BellIcon,
  ShieldCheckIcon,
  UserIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  KeyIcon,
  Cog6ToothIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import UserManager from "../feature/user/UserManager";
import BranchManager from "../components/settings/BranchManager";
import ChangePassword from "../components/settings/ChangePassword";
import ICMSettings from "../components/settings/ICMSettings";
import TimelineSettings from "../components/settings/TimelineSettings";
import PageWrapper from "../components/common/PageWrapper";
import HeaderSection from "../components/HeaderSection";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

interface TabItem {
  name: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  component: React.ReactNode;
}

const Settings: React.FC = () => {
  const dispatch = useDispatch();
  const { currentTheme } = useSelector((state: RootState) => state.theme);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleThemeChange = (theme: "light" | "dark" | "bw") => {
    dispatch(setTheme(theme));
  };

  const tabs: TabItem[] = [
    {
      name: "Security",
      Icon: KeyIcon,
      component: <ChangePassword />,
    },
    // {
    //   name: "ICM Settings",
    //   Icon: BuildingOfficeIcon,
    //   component: <ICMSettings />,
    // },
    // {
    //   name: "Timeline",
    //   Icon: ClockIcon,
    //   component: <TimelineSettings />,
    // },
    // {
    //   name: "Notifications",
    //   Icon: BellIcon,
    //   component: <NotificationSettings />,
    // },
    // {
    //   name: "Preferences",
    //   Icon: Cog6ToothIcon,
    //   component: <PreferenceSettings />,
    // },
  ];

  return (
    <PageWrapper>
      <div className="flex justify-between items-center mb-6 ">
        {/* <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Settings
        </h1> */}
        <HeaderSection
          title="Settings"
        />
      </div>

      <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex space-x-1 border-b border-secondary-200 dark:border-secondary-700 px-4">
            {tabs.map((tab) => (
              <Tab
                key={tab.name}
                className={({ selected }) =>
                  `py-4 px-6 text-sm font-medium focus:outline-none ${
                    selected
                      ? "text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400"
                      : "text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-300"
                  }`
                }
              >
                <div className="flex items-center justify-center space-x-2">
                  <tab.Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </div>
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="p-6">
            {tabs.map((tab, idx) => (
              <Tab.Panel key={idx}>{tab.component}</Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </PageWrapper>
  );
};

const NotificationSettings: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
          Notification Preferences
        </h3>
        <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
          Choose how you want to be notified about updates.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="email_notifications"
              type="checkbox"
              className="h-4 w-4 text-primary-600 border-secondary-300 rounded"
              defaultChecked
            />
          </div>
          <div className="ml-3">
            <label
              htmlFor="email_notifications"
              className="text-sm font-medium text-secondary-900 dark:text-white"
            >
              Email Notifications
            </label>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Receive email notifications about order updates and status
              changes.
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="push_notifications"
              type="checkbox"
              className="h-4 w-4 text-primary-600 border-secondary-300 rounded"
              defaultChecked
            />
          </div>
          <div className="ml-3">
            <label
              htmlFor="push_notifications"
              className="text-sm font-medium text-secondary-900 dark:text-white"
            >
              Push Notifications
            </label>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Receive push notifications on your desktop or mobile device.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreferenceSettings: React.FC = () => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
          Display Preferences
        </h3>
        <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
          Customize how information is displayed in your dashboard.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="date_format"
            className="block text-sm font-medium text-secondary-900 dark:text-white"
          >
            Date Format
          </label>
          <select
            id="date_format"
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-secondary-300 dark:border-secondary-600 rounded-md dark:bg-secondary-700"
            defaultValue="MM/DD/YYYY"
          >
            <option>MM/DD/YYYY</option>
            <option>DD/MM/YYYY</option>
            <option>YYYY-MM-DD</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="timezone"
            className="block text-sm font-medium text-secondary-900 dark:text-white"
          >
            Timezone
          </label>
          <select
            id="timezone"
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-secondary-300 dark:border-secondary-600 rounded-md dark:bg-secondary-700"
            defaultValue="UTC"
          >
            <option>UTC</option>
            <option>IST</option>
            <option>EST</option>
            <option>PST</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default Settings;
